import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
} from 'reactstrap';
import WavePlayer from '../WavePlayer';

interface ApproveTrackModalProps {
  isOpen: boolean;
  onClose: () => void;
  track: any;
  firebase: any;
  onReviewSubmitted?: () => void;
}

const ApproveTrackModal: React.FC<ApproveTrackModalProps> = ({
  isOpen,
  onClose,
  track,
  firebase,
  onReviewSubmitted,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const getPlaybackUrl = (): string | null => {
    if (!track) return null;
    const { mp3Files, defaultMp3Id } = track;
    if (!mp3Files || mp3Files.length === 0) return null;
    if (defaultMp3Id) {
      const defaultFile = mp3Files.find((f: any) => f.storageRef === defaultMp3Id);
      if (defaultFile?.downloadUrl) return defaultFile.downloadUrl;
    }
    return mp3Files[0]?.downloadUrl || null;
  };

  const handleSubmit = async (decision: 'approved' | 'rejected') => {
    if (!firebase || !track) return;

    setSubmitting(true);
    setError(null);
    try {
      const currentUser = firebase.auth.currentUser;
      const success = await firebase.submitReview(
        track.sourceOrgId,
        track.sourceTrackId || track.id,
        track.reviewId,
        decision,
        {
          rejectionReason: decision === 'rejected' ? rejectionReason : undefined,
          reviewerName: currentUser?.displayName || currentUser?.email || 'Unknown',
          reviewerEmail: currentUser?.email || null,
          reviewedVia: 'in-app' as const,
        }
      );

      if (success) {
        onReviewSubmitted?.();
        onClose();
        return; // Component will unmount, don't update state
      } else {
        setError('Failed to submit review. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Error submitting review');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRejecting(false);
    setRejectionReason('');
    setError(null);
    onClose();
  };

  if (!track) return null;

  const playbackUrl = getPlaybackUrl();

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      style={{
        maxWidth: '550px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>Review Spot</span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        {/* Track info */}
        <h5 style={{ color: 'var(--accent-1-color)', marginBottom: '0.25rem' }}>
          {track.trackName}
        </h5>
        <small style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          From: {track.sourceOrgName || 'Unknown'}
        </small>

        {/* Metadata pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', margin: '0.75rem 0' }}>
          {track.language && Array.isArray(track.language) && track.language.length > 0 && (
            <span style={{
              padding: '1px 8px', fontSize: '0.75rem', borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)',
            }}>
              {track.language.map((l: any) => l.label).join(', ')}
            </span>
          )}
          {track.client && (
            <span style={{
              padding: '1px 8px', fontSize: '0.75rem', borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)',
            }}>
              Client: {track.client.label}
            </span>
          )}
          {track.agency && (
            <span style={{
              padding: '1px 8px', fontSize: '0.75rem', borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)',
            }}>
              Agency: {track.agency.label}
            </span>
          )}
        </div>

        {/* Audio playback */}
        {playbackUrl && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <WavePlayer
              id={`approve-modal-${track.id}`}
              file={playbackUrl}
              isActiveTrack={true}
              noAutoplay={true}
            />
          </div>
        )}

        {/* Rejection reason */}
        {rejecting && (
          <div style={{ marginBottom: '1rem' }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Reason for rejection</Label>
            <Input
              type="textarea"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason..."
              rows={3}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                borderRadius: '4px',
                resize: 'vertical',
              }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '0.75rem',
            backgroundColor: 'rgba(255, 80, 80, 0.1)',
            borderRadius: '6px',
            color: '#ff5050',
            fontSize: '0.9rem',
            marginBottom: '0.5rem',
          }}>
            {error}
          </div>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
        }}
      >
        {rejecting ? (
          <>
            <Button
              className="btn-danger-custom"
              onClick={() => handleSubmit('rejected')}
              disabled={submitting}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                {submitting ? 'Rejecting...' : 'Confirm Rejection'}
              </span>
            </Button>
            <Button
              className="btn-primary-custom"
              onClick={() => { setRejecting(false); setRejectionReason(''); }}
              disabled={submitting}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>Back</span>
            </Button>
          </>
        ) : (
          <>
            <Button
              className="btn-success-custom"
              onClick={() => handleSubmit('approved')}
              disabled={submitting}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                {submitting ? 'Approving...' : 'Approve'}
              </span>
            </Button>
            <Button
              className="btn-danger-custom"
              onClick={() => setRejecting(true)}
              disabled={submitting}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>Reject</span>
            </Button>
            <Button className="btn-info-custom" onClick={handleClose} disabled={submitting}>
              <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ApproveTrackModal;
